import { useEffect, useRef, useState } from "react";
import {
    Phone,
    PhoneDisconnect,
    Backspace,
    MicrophoneSlash,
    Microphone,
    CaretDown,
    X,
    ArrowsLeftRight,
    Pause,
    Play,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import IncomingCallModal from "@/components/IncomingCallModal";
import { toast } from "sonner";
import twilioDevice from "@/lib/twilioDevice";
import exotelDevice from "@/lib/exotelDevice";
import plivoDevice from "@/lib/plivoDevice";
import { useAuth } from "@/context/AuthContext";

const KEYS = [
    ["1", ""], ["2", "ABC"], ["3", "DEF"],
    ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
    ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
    ["+", ""], ["0", ""], ["#", ""],
];

const fmtDuration = (s) => {
    const m = String(Math.floor(s / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${m}:${sec}`;
};

const cleanNumber = (raw) => (raw || "").replace(/[^\d+*#]/g, "").slice(0, 20);

const DISPOSITIONS = [
    "Interested",
    "Not Interested",
    "Callback Requested",
    "Left Voicemail",
    "No Answer",
    "Wrong Number",
    "Do Not Call",
    "Sale Closed",
];

export default function FloatingDialer({ onCallCompleted, embedded = false }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(embedded);
    const [minimized, setMinimized] = useState(false);
    const [number, setNumber] = useState("");
    const [status, setStatus] = useState("idle");
    const [activeCall, setActiveCall] = useState(null);
    const [muted, setMuted] = useState(false);
    const [onHold, setOnHold] = useState(false);
    const [holdBusy, setHoldBusy] = useState(false);
    const [notes, setNotes] = useState("");
    const [disposition, setDisposition] = useState("");
    const [elapsed, setElapsed] = useState(0);
    const [busy, setBusy] = useState(false);
    const [provider, setProvider] = useState("mock");
    const [exotelMode, setExotelMode] = useState("number");
    const [twilioReady, setTwilioReady] = useState(false);
    const [exotelReady, setExotelReady] = useState(false);
    const [plivoReady, setPlivoReady] = useState(false);
    const [incoming, setIncoming] = useState(null);
    const [transferOpen, setTransferOpen] = useState(false);
    const [onlineAgents, setOnlineAgents] = useState([]);
    const [activeKey, setActiveKey] = useState(null);
    const flashRef = useRef(null);
    const timerRef = useRef(null);
    const pollRef = useRef(null);
    const startedAtRef = useRef(null);
    const incomingFromRef = useRef("");
    const campaignModeRef = useRef(false);

    const inCall = ["initializing", "ringing", "connected"].includes(status);

    // A real WebRTC device is needed only for Twilio, Plivo, or Exotel "webrtc" mode.
    // Exotel "number" mode (number-connect) rings the agent's phone — no browser device.
    const exotelWebrtc = provider === "exotel" && exotelMode === "webrtc";
    const isPlivo = provider === "plivo";
    const needsDevice = provider === "twilio" || isPlivo || exotelWebrtc;
    const rtcReady = provider === "twilio" ? twilioReady : isPlivo ? plivoReady : exotelWebrtc ? exotelReady : true;
    // Active WebRTC client used for incoming-call controls (accept/reject/mute/disconnect).
    const rtc = isPlivo ? plivoDevice : exotelWebrtc ? exotelDevice : twilioDevice;

    // --- Lifecycle: load provider, register Twilio Device, listen to bus events ---
    useEffect(() => {
        if (!user) return;
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get("/settings");
                if (!mounted) return;
                setProvider(data.active_provider);
                const mode = data.exotel_call_mode || "number";
                setExotelMode(mode);
                if (data.active_provider === "twilio") {
                    try {
                        await twilioDevice.ensureDevice(`agent_${user.id}`);
                        if (mounted) setTwilioReady(twilioDevice.isReady());
                    } catch (e) {
                        if (mounted) toast.error("Dialer couldn't connect to Twilio", { description: "Check your Twilio credentials in Settings → Providers, then reload.", duration: 6000 });
                    }
                } else if (data.active_provider === "exotel" && mode === "webrtc") {
                    try {
                        await exotelDevice.ensureDevice();
                        if (mounted) setExotelReady(exotelDevice.isReady());
                    } catch (e) {
                        if (mounted) toast.error("Dialer couldn't connect to Exotel", { description: "Check your Exotel VoIP settings in Settings → Providers, then reload.", duration: 6000 });
                    }
                } else if (data.active_provider === "plivo") {
                    try {
                        await plivoDevice.ensureDevice();
                        if (mounted) setPlivoReady(plivoDevice.isReady());
                    } catch (e) {
                        if (mounted) toast.error("Dialer couldn't connect to Plivo", { description: "Check your Plivo credentials in Settings → Providers, then reload.", duration: 6000 });
                    }
                }
            } catch (_e) {}
        })();

        const handleBusEvent = ({ event, ...payload }) => {
            if (event === "incoming") {
                setIncoming(payload);
                incomingFromRef.current = payload.from || "";
                if (payload.from) setNumber(cleanNumber(payload.from));
                // Predictive routed calls are auto-accepted; capture disposition in
                // the workspace (single disposition) → suppress the dialer wrap-up.
                campaignModeRef.current = !!payload.autoAccepted;
                setOpen(true);
                setMinimized(false);
                if (payload.from) {
                    // Detect a transferred call (conference-routed calls can't carry
                    // SDK custom params, so read it from the live call record).
                    api.get("/calls/find-active", { params: { from_number: payload.from } })
                        .then(({ data }) => {
                            if (data?.transferred_from && data?.transferred_at) {
                                const age = Date.now() - new Date(data.transferred_at).getTime();
                                if (age < 120000) {
                                    setIncoming((i) => (i ? { ...i, isTransfer: true, transferFrom: data.transferred_from } : i));
                                }
                            }
                        })
                        .catch(() => {});
                    api.get("/integrations/salesforce/lookup", { params: { phone: payload.from } })
                        .then(({ data }) => {
                            if (data?.matched) {
                                setIncoming((i) => (i ? { ...i, name: data.name, company: data.company, type: data.type } : i));
                            }
                        })
                        .catch(() => {});
                }
            }
            if (event === "call:accept" && payload.inbound) {
                setStatus("connected");
                setIncoming(null);
                resolveInboundCall(incomingFromRef.current);
            }
            // Outbound leg answered by the remote party → mark connected immediately
            // (don't wait for the 2s status poll).
            if (event === "call:accept" && !payload.inbound) {
                setStatus("connected");
            }
            if (event === "call:error") {
                // The provider SDK is authoritative: the leg failed (e.g. No Answer / the
                // caller hung up before pickup). Clear the incoming popup AND stop the
                // backend status poll so it can't overwrite this terminal state back to
                // "ringing", then finalize the call.
                setIncoming(null);
                if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
                setStatus((s) => (s === "idle" ? "idle" : "failed"));
                if (payload?.error) toast.error(payload.error);
                onCallCompleted?.();
            }
            if (event === "call:disconnect") {
                setIncoming(null);
                // SDK says the (outbound/answered) leg ended — finalize now instead of
                // waiting on the lagging backend poll.
                if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
                setStatus((s) => (["initializing", "ringing", "connected"].includes(s) ? "completed" : s));
                onCallCompleted?.();
            }
            if (event === "call:cancel" || event === "call:reject") {
                setIncoming(null);
            }
            if (event === "dialer:open") {
                setOpen(true);
                setMinimized(false);
                campaignModeRef.current = !!payload?.campaign;
                if (payload?.number) setNumber(cleanNumber(payload.number));
                if (payload?.autoDial && payload?.number) {
                    // One-click dial from Campaign workspace / other surfaces.
                    setTimeout(() => placeCall(cleanNumber(payload.number)), 150);
                }
            }
        };
        const offTwilio = twilioDevice.on((e) => {
            if (e.event === "registered") return setTwilioReady(true);
            if (e.event === "unregistered") return setTwilioReady(false);
            handleBusEvent(e);
        });
        const offExotel = exotelDevice.on((e) => {
            if (e.event === "registered") return setExotelReady(true);
            if (e.event === "unregistered") return setExotelReady(false);
            handleBusEvent(e);
        });
        const offPlivo = plivoDevice.on((e) => {
            if (e.event === "registered") return setPlivoReady(true);
            if (e.event === "unregistered") return setPlivoReady(false);
            handleBusEvent(e);
        });
        return () => { mounted = false; offTwilio(); offExotel(); offPlivo(); };
    }, [user?.id]);

    useEffect(() => () => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (pollRef.current) clearInterval(pollRef.current);
    }, []);

    // In a Campaign run the disposition is captured in the Agent Workspace, so
    // suppress the dialer's own wrap-up panel and quietly reset when the call ends.
    useEffect(() => {
        if (status === "completed" && campaignModeRef.current) {
            onCallCompleted?.();
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        if (status === "connected" && !timerRef.current) {
            // Keep an already-seeded start (e.g. restored after a remount) so the
            // timer reflects the true talk time, not the time since this mount.
            if (!startedAtRef.current) startedAtRef.current = Date.now();
            timerRef.current = setInterval(() => {
                setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
            }, 1000);
        }
        if (!inCall && timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            startedAtRef.current = null;
        }
    }, [status, inCall]);

    // Restore an in-progress call after a remount. Each page mounts its OWN
    // AppShell → FloatingDialer, so navigating (or a reopen) unmounts this
    // component and wipes activeCall/status — reopening then showed a fresh
    // dialpad even though the call was still live. The WebRTC device singletons
    // (twilio/plivo/exotel) survive the remount, so we only need to re-hydrate
    // the UI + re-attach the status watchdog against the agent's own live call.
    useEffect(() => {
        if (!user || embedded) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/calls/find-active");
                if (cancelled || !data || !data.id) return;
                // Only restore THIS agent's genuinely connected call.
                if (data.status !== "connected") return;
                if (data.user_id && data.user_id !== user.id) return;
                setActiveCall(data);
                setStatus("connected");
                setOnHold(!!data.on_hold);
                const other = data.direction === "inbound" ? data.from_number : data.to_number;
                if (other) setNumber(cleanNumber(other));
                if (data.started_at) {
                    const startedMs = new Date(data.started_at).getTime();
                    if (!Number.isNaN(startedMs)) {
                        startedAtRef.current = startedMs;
                        setElapsed(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
                    }
                }
                setOpen(true);
                setMinimized(true); // surface the live mini-bar so the agent sees the ongoing call
                startPolling(data.id);
            } catch (_e) {}
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    // Physical keyboard → dialpad. Type digits anywhere in the open dialer and
    // they enter the number with the same visual key-press feedback as tapping.
    useEffect(() => {
        if (inCall) return;
        if (!open && !embedded) return;
        const handler = (e) => {
            const t = e.target;
            const tag = (t?.tagName || "").toLowerCase();
            // Let the number input handle its own typing; ignore other fields.
            if (t?.getAttribute?.("data-testid") === "dialer-number-input") return;
            if (tag === "textarea" || tag === "select" || tag === "input") return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === "Enter") { if (number && !busy && !(needsDevice && !rtcReady)) placeCall(); return; }
            if (e.key === "Backspace") { e.preventDefault(); backspace(); return; }
            if (e.key === "+") { e.preventDefault(); press("+"); return; }
            if (/^[0-9*#]$/.test(e.key)) { e.preventDefault(); press(e.key); return; }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, embedded, inCall, number, busy, provider, twilioReady]);

    const press = (k) => {
        if (inCall) return;
        if (k === "+") {
            setNumber((n) => (n.length === 0 ? "+" : n));
            return;
        }
        setNumber((n) => (n + k).slice(0, 20));
    };
    const backspace = () => setNumber((n) => n.slice(0, -1));

    const startPolling = (callId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/calls/${callId}/status`);
                setStatus(data.status);
                if (["completed", "failed", "no-answer"].includes(data.status)) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    // The remote party ended the call (or it dropped). Tear down the
                    // local leg + reset so the agent never has to hang up a dead call.
                    rtc.disconnect();
                    onCallCompleted?.();
                }
            } catch (_e) {}
        }, 2000);
    };

    const placeCall = async (overrideNumber) => {
        const dialNumber = (typeof overrideNumber === "string" && overrideNumber) ? overrideNumber : number;
        if (!dialNumber || busy) return;
        if (dialNumber !== number) setNumber(dialNumber);
        setBusy(true);
        setElapsed(0);
        setMuted(false);
        setOnHold(false);
        setNotes("");
        setDisposition("");
        try {
            const { data: call } = await api.post("/calls/initiate", { to_number: dialNumber });
            setActiveCall(call);
            setStatus(call.status || "initializing");
            startPolling(call.id);
            if (call.provider === "twilio") {
                if (!twilioDevice.isReady()) {
                    try { await twilioDevice.ensureDevice(`agent_${user.id}`); } catch (_e) {}
                }
                try {
                    await twilioDevice.connect({ to_number: dialNumber, internal_call_id: call.id });
                } catch (e) {
                    toast.error("Connect failed: " + (e?.message || e));
                    setStatus("failed");
                }
            } else if (call.provider === "exotel" && exotelWebrtc) {
                // WebRTC mode only: register our SIP device so Exotel can ring it.
                // In number-connect mode the backend rings the agent's phone — no device.
                try {
                    await exotelDevice.connect();
                } catch (e) {
                    toast.error("Exotel connect failed: " + (e?.message || e));
                    setStatus("failed");
                }
            } else if (call.provider === "plivo") {
                // Browser-initiated: the Plivo SDK places the call directly. Dial the
                // SERVER-NORMALIZED number (call.to_number, e.g. +91… for India) — the
                // raw dialpad value (bare 10-digit) makes Plivo fail the call.
                if (!plivoDevice.isReady()) {
                    try { await plivoDevice.ensureDevice(); } catch (_e) {}
                }
                try {
                    await plivoDevice.connect({ to_number: call.to_number || dialNumber, internal_call_id: call.id });
                } catch (e) {
                    toast.error("Plivo connect failed: " + (e?.message || e));
                    setStatus("failed");
                }
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to start call");
            setStatus("failed");
        } finally {
            setBusy(false);
        }
    };

    const hangup = async () => {
        if (!activeCall) {
            // inbound case — no DB activeCall record on the client
            rtc.disconnect();
            setStatus("completed");
            setTimeout(() => onCallCompleted?.(), 500);
            return;
        }
        setBusy(true);
        try {
            if (activeCall.provider === "twilio") twilioDevice.disconnect();
            else if (activeCall.provider === "exotel") exotelDevice.disconnect();
            else if (activeCall.provider === "plivo") plivoDevice.disconnect();
            const { data } = await api.post(`/calls/${activeCall.id}/hangup`);
            setStatus(data.status);
            if (notes.trim()) await api.patch(`/calls/${activeCall.id}/notes`, { notes });
            toast.success("Call ended");
            onCallCompleted?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to end call");
        } finally {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            setBusy(false);
        }
    };

    async function resolveInboundCall(fromNum) {
        // Inbound calls aren't initiated by this client, so look up the live DB
        // record to enable mute/transfer/notes against the right call id — and
        // start the status watchdog so the UI auto-resets when the call ends
        // (the Twilio SDK 'disconnect' event can be missed in conference/bridge
        // scenarios, leaving the agent stuck on a dead call).
        try {
            const { data } = await api.get("/calls/find-active", { params: { from_number: fromNum || "" } });
            if (data && data.id) {
                let call = data;
                // Attribute this inbound call to the agent who accepted it so the
                // Salesforce Task logs the correct owner (harmless no-op otherwise).
                try {
                    const { data: claimed } = await api.post(`/calls/${data.id}/claim`);
                    if (claimed && claimed.id) call = claimed;
                } catch (_e) { /* attribution is best-effort */ }
                setActiveCall(call);
                startPolling(call.id);
            }
        } catch (_e) {}
    }

    const toggleMute = async () => {
        const next = !muted;
        setMuted(next);
        rtc.mute(next); // client-side; works for inbound + outbound calls
        if (activeCall) {
            try { await api.post(`/calls/${activeCall.id}/mute`, { muted: next }); } catch (_e) {}
        }
    };

    const toggleHold = async () => {
        if (!activeCall || holdBusy) return;
        setHoldBusy(true);
        const next = !onHold;
        try {
            await api.post(`/calls/${activeCall.id}/${next ? "hold" : "resume"}`);
            setOnHold(next);
            toast.success(next ? "Customer on hold" : "Call resumed");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hold failed");
        } finally {
            setHoldBusy(false);
        }
    };

    const openTransfer = async () => {
        const next = !transferOpen;
        setTransferOpen(next);
        if (next) {
            try {
                const { data } = await api.get("/agents/presence");
                setOnlineAgents((data || []).filter((a) => a.status === "online" && a.id !== user.id));
            } catch (_e) { setOnlineAgents([]); }
        }
    };

    const doTransfer = async (targetId) => {
        if (!activeCall) return;
        const agentCallSid = twilioDevice.getActiveCallSid();
        try {
            const { data } = await api.post(`/calls/${activeCall.id}/transfer`, {
                target_user_id: targetId,
                agent_call_sid: agentCallSid,
            });
            toast.success(`Call transferred to ${data.transferred_to}`);
            setTransferOpen(false);
            // Stop the status watchdog first — otherwise it would re-read the
            // (still-live) customer leg and flip our UI back to "connected".
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            twilioDevice.disconnect();
            setStatus("completed");
            onCallCompleted?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Transfer failed");
        }
    };

    const reset = () => { setStatus("idle"); setActiveCall(null); setNumber(""); setNotes(""); setDisposition(""); setElapsed(0); };

    const saveNotes = async () => {
        if (!activeCall) return;
        try { await api.patch(`/calls/${activeCall.id}/notes`, { notes }); toast.success("Notes saved"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save notes"); }
    };

    const saveWrapup = async () => {
        if (!activeCall) { reset(); return; }
        if (!disposition) { toast.error("Pick a call outcome first"); return; }
        setBusy(true);
        try {
            await api.post(`/calls/${activeCall.id}/wrapup`, { disposition, notes });
            toast.success("Call wrap-up saved & synced to Salesforce");
            onCallCompleted?.();
            reset();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to save wrap-up");
        } finally {
            setBusy(false);
        }
    };

    const onKeyPress = (e) => {
        if (e.key === "Enter" && number && !inCall) placeCall();
    };

    // Hide entirely on auth pages or when no user
    if (!user) return null;

    // -------------------- Render --------------------

    // 1) Incoming modal (always center-screen, full-attention)
    if (incoming) {
        return (
            <IncomingCallModal
                from={incoming.from}
                name={incoming.name}
                company={incoming.company}
                type={incoming.type}
                isTransfer={incoming.isTransfer}
                transferFrom={incoming.transferFrom}
                onAccept={() => rtc.acceptIncoming()}
                onReject={() => { rtc.rejectIncoming(); setIncoming(null); }}
            />
        );
    }

    // 2) Live call but the full panel isn't showing (closed OR minimized) →
    //    show the mini-bar so the agent always sees the ongoing call and can
    //    restore full controls with one tap.
    if (!embedded && inCall && (minimized || !open)) {
        return (
            <button
                data-testid="restore-dialer"
                onClick={() => { setOpen(true); setMinimized(false); }}
                className="fixed bottom-6 right-6 z-40 flex items-center gap-3 rounded-full bg-ink px-5 py-3 text-white shadow-xl"
            >
                <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500">
                    <Phone size={16} weight="fill" />
                </span>
                <div className="text-left">
                    <div className="text-[10px] uppercase tracking-widest text-white/60">{onHold ? "on hold" : status}</div>
                    <div className="tabular font-heading text-base font-semibold">{number}</div>
                </div>
                <div className="tabular font-heading text-sm font-semibold">{fmtDuration(elapsed)}</div>
            </button>
        );
    }

    // 3) Closed with no active call → floating action button
    if (!open && !embedded) {
        return (
            <button
                data-testid="open-dialer-fab"
                onClick={() => setOpen(true)}
                className="fixed bottom-6 right-6 z-40 grid h-16 w-16 place-items-center overflow-hidden rounded-full border border-stone-200 bg-white shadow-xl shadow-brand/30 transition-all hover:-translate-y-0.5"
            >
                <span className="absolute inset-0 -z-10 animate-pulse-ring rounded-full bg-brand/40" />
                <img src="/inspiro-connect-mark.png" alt="Inspiro Connect" className="h-11 w-11 object-contain" />
            </button>
        );
    }

    // 4) Open dialer panel
    return (
        <div
            data-testid="dialer-panel"
            className={embedded
                ? "flex h-full w-full flex-col bg-white"
                : "fixed bottom-6 right-6 z-40 flex w-[360px] flex-col rounded-3xl border border-stone-200 bg-white shadow-2xl shadow-black/10"}
            style={embedded ? { height: "100%" } : { maxHeight: "min(700px, calc(100vh - 48px))" }}
        >
            {/* Header */}
            {!embedded && (
            <div className="flex items-center justify-between border-b border-stone-100 px-5 py-3">
                <div className="flex items-center gap-2">
                    <div className="grid h-8 w-8 place-items-center overflow-hidden rounded-xl border border-stone-200 bg-white">
                        <img src="/inspiro-connect-mark.png" alt="Inspiro Connect" className="h-full w-full object-contain p-0.5" />
                    </div>
                    <div className="min-w-0">
                        <div className="truncate font-heading text-sm font-bold leading-none" data-testid="dialer-agent-name">{user?.name || "Inspiro Dialer"}</div>
                        <div className="mt-1 flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] text-stone2" data-testid="dialer-provider">
                            via {provider}
                            {needsDevice && (
                                <span className={`inline-block h-1.5 w-1.5 rounded-full ${rtcReady ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`} />
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {inCall && !embedded && (
                        <button
                            data-testid="minimize-dialer"
                            onClick={() => setMinimized(true)}
                            className="grid h-7 w-7 place-items-center rounded-lg text-stone2 hover:bg-stone-100"
                            title="Minimize"
                        >
                            <CaretDown size={14} />
                        </button>
                    )}
                    {!embedded && (
                        <button
                            data-testid="close-dialer"
                            onClick={() => setOpen(false)}
                            className="grid h-7 w-7 place-items-center rounded-lg text-stone2 hover:bg-stone-100"
                            title={inCall ? "Hide (call continues)" : "Close"}
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>
            )}

            {/* Body */}
            <div className={embedded ? "flex flex-1 flex-col gap-2 overflow-y-auto p-3" : "flex flex-1 flex-col gap-4 overflow-y-auto p-5"}>
                {/* Number + status */}
                <div className={embedded ? "px-2 py-1 text-center" : "px-2 py-3 text-center"}>
                    <div className={embedded ? "mb-1.5 flex items-center justify-center" : "mb-3 flex items-center justify-center"}>
                        <StatusPill status={status} testId="dialer-status" />
                    </div>
                    <input
                        data-testid="dialer-number-input"
                        type="tel"
                        value={number}
                        onChange={(e) => setNumber(cleanNumber(e.target.value))}
                        onKeyDown={onKeyPress}
                        placeholder="Enter a number"
                        disabled={inCall}
                        className={`tabular w-full bg-transparent text-center font-mono ${embedded ? "text-2xl" : "text-3xl"} font-medium tracking-tight text-ink outline-none placeholder:text-stone-300`}
                    />
                    {inCall && (
                        <div className="mt-2 tabular font-mono text-sm font-semibold text-emerald-600" data-testid="call-timer">
                            {fmtDuration(elapsed)}
                        </div>
                    )}
                </div>

                {/* Dialpad OR in-call controls */}
                {!inCall ? (
                    <>
                    <div className="grid grid-cols-3 gap-2">
                        {KEYS.map(([k, sub]) => (
                            <button
                                key={k}
                                data-testid={`dialer-key-${k}`}
                                onClick={() => press(k)}
                                className={`group relative flex ${embedded ? "h-12" : "h-14"} select-none flex-col items-center justify-center rounded-2xl border transition-all duration-150 ${
                                    activeKey === k
                                        ? "scale-95 border-brand bg-brand/10 shadow-inner"
                                        : "border-stone-200 bg-white hover:-translate-y-0.5 hover:border-ink hover:shadow-sm active:scale-95"
                                }`}
                            >
                                <span className={`font-heading ${embedded ? "text-lg" : "text-xl"} font-semibold leading-none transition-colors ${activeKey === k ? "text-brand" : "text-ink"}`}>{k}</span>
                                {sub && <span className="mt-0.5 text-[8px] font-bold tracking-[0.2em] text-stone-400">{sub}</span>}
                            </button>
                        ))}
                    </div>
                    <div className="mt-0.5 flex items-center justify-center gap-1.5 text-[10px] font-medium tracking-wide text-stone-400" data-testid="keyboard-hint">
                        <kbd className="rounded border border-stone-200 bg-stone-50 px-1 py-px font-mono text-[9px] text-stone2">0-9</kbd>
                        type on your keyboard ·
                        <kbd className="rounded border border-stone-200 bg-stone-50 px-1 py-px font-mono text-[9px] text-stone2">Enter</kbd>
                        to call
                    </div>
                    </>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            data-testid="mute-button"
                            onClick={toggleMute}
                            className={`flex h-12 items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-all ${
                                muted ? "border-ink bg-ink text-white" : "border-stone-200 bg-white hover:border-ink"
                            }`}
                        >
                            {muted ? <MicrophoneSlash size={16} weight="fill" /> : <Microphone size={16} />}
                            {muted ? "Muted" : "Mute"}
                        </button>
                        <div className="relative">
                            <button
                                data-testid="transfer-button"
                                onClick={openTransfer}
                                disabled={!activeCall}
                                className={`flex h-12 w-full items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-all disabled:opacity-40 ${
                                    transferOpen ? "border-ink bg-ink text-white" : "border-stone-200 bg-white hover:border-ink"
                                }`}
                            >
                                <ArrowsLeftRight size={16} /> Transfer
                            </button>
                            {transferOpen && (
                                <div data-testid="transfer-menu" className="absolute bottom-full left-0 z-30 mb-1 max-h-44 w-full overflow-y-auto rounded-xl border border-stone-200 bg-white shadow-xl">
                                    <div className="border-b border-stone-100 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-stone2">
                                        Online agents
                                    </div>
                                    {onlineAgents.length === 0 ? (
                                        <div className="px-3 py-3 text-xs text-stone2">No other agents online</div>
                                    ) : (
                                        onlineAgents.map((a) => (
                                            <button
                                                key={a.id}
                                                data-testid={`transfer-to-${a.id}`}
                                                onClick={() => doTransfer(a.id)}
                                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-stone-50"
                                            >
                                                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                                <span className="truncate font-medium">{a.name}</span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {inCall && isPlivo && (
                    <button
                        data-testid="hold-button"
                        onClick={toggleHold}
                        disabled={!activeCall || holdBusy}
                        className={`mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-all disabled:opacity-40 ${
                            onHold ? "border-amber-500 bg-amber-500 text-white" : "border-stone-200 bg-white hover:border-ink"
                        }`}
                    >
                        {onHold ? <Play size={16} weight="fill" /> : <Pause size={16} weight="fill" />}
                        {holdBusy ? "…" : onHold ? "Resume" : "Hold"}
                    </button>
                )}

                {/* Live notes during call */}
                {inCall && (
                    <div className="rounded-xl border border-stone-200 bg-stone-50 p-3">
                        <div className="mb-1 flex items-center justify-between">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone2">Notes</div>
                            <button data-testid="save-notes-button" onClick={saveNotes} className="text-[10px] font-bold uppercase tracking-widest text-brand hover:text-brand-hover">Save</button>
                        </div>
                        <textarea
                            data-testid="call-notes-input"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            placeholder="Follow-up next Tuesday…"
                            className="w-full resize-none rounded-lg border border-stone-200 bg-white p-2 text-xs outline-none focus:ring-2 focus:ring-brand/40"
                        />
                    </div>
                )}

                {/* Wrap-up after call ends */}
                {status === "completed" && !campaignModeRef.current && (
                    <div data-testid="wrapup-panel" className="rounded-xl border border-brand/30 bg-brand/5 p-3">
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-brand">Call wrap-up</div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Outcome / Disposition</label>
                        <select
                            data-testid="wrapup-disposition"
                            value={disposition}
                            onChange={(e) => setDisposition(e.target.value)}
                            className="mt-1 mb-3 w-full rounded-lg border border-stone-300 bg-white px-2 py-2 text-sm outline-none focus:border-ink"
                        >
                            <option value="">Select outcome…</option>
                            {DISPOSITIONS.map((d) => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Notes</label>
                        <textarea
                            data-testid="wrapup-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            placeholder="What happened on the call?"
                            className="mt-1 w-full resize-none rounded-lg border border-stone-200 bg-white p-2 text-xs outline-none focus:ring-2 focus:ring-brand/40"
                        />
                        <p className="mt-1.5 text-[10px] text-stone2">Synced to the matching Salesforce contact's activity.</p>
                    </div>
                )}
            </div>

            {/* Footer actions */}
            <div className={embedded ? "flex items-center justify-center gap-3 border-t border-stone-100 px-3 py-3" : "flex items-center justify-center gap-3 border-t border-stone-100 px-5 py-4"}>
                {!inCall && status !== "completed" && status !== "failed" && (
                    <>
                        <button
                            onClick={backspace}
                            data-testid="backspace-button"
                            disabled={!number}
                            className={`grid h-10 w-10 place-items-center rounded-full border bg-white transition-all disabled:opacity-40 ${
                                activeKey === "back" ? "scale-95 border-brand text-brand" : "border-stone-200 text-stone2 hover:border-ink hover:text-ink"
                            }`}
                        >
                            <Backspace size={16} />
                        </button>
                        <button
                            data-testid="outbound-call-button"
                            onClick={placeCall}
                            disabled={!number || busy || (needsDevice && !rtcReady)}
                            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-brand font-heading text-sm font-semibold text-white shadow-lg shadow-brand/25 transition-all hover:-translate-y-0.5 hover:bg-brand-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Phone size={18} weight="fill" />
                            Call
                        </button>
                    </>
                )}

                {inCall && (
                    <button
                        data-testid="hangup-button"
                        onClick={hangup}
                        disabled={busy}
                        className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-red-500 font-heading text-sm font-semibold text-white shadow-lg shadow-red-500/25 transition-all hover:-translate-y-0.5 hover:bg-red-600 active:scale-[0.98]"
                    >
                        <PhoneDisconnect size={18} weight="fill" />
                        Hang Up
                    </button>
                )}

                {status === "completed" && (
                    <>
                        <button
                            data-testid="wrapup-save-button"
                            onClick={saveWrapup}
                            disabled={busy}
                            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-brand font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-50"
                        >
                            Save & Log
                        </button>
                        <button
                            data-testid="new-call-button"
                            onClick={reset}
                            className="rounded-full border border-stone-300 bg-white px-5 py-2.5 font-heading text-sm font-semibold text-stone2 transition-all hover:border-ink hover:text-ink"
                        >
                            Skip
                        </button>
                    </>
                )}

                {status === "failed" && (
                    <button
                        data-testid="new-call-button"
                        onClick={reset}
                        className="rounded-full bg-ink px-6 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
                    >
                        New Call
                    </button>
                )}
            </div>
        </div>
    );
}
